import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../layouts/default";
import { Button, Spinner, Table, Row, Col, Form, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import SmartPagination from "../Report/EmailCampaignRunReport/DynamicSmartPagination";
import {
  fetchEmailVendors,
  createEmailVendor,
  updateEmailVendor,
  testEmailVendor,
} from "../../store/slices/emailVendors/emailVendorsSlice";
import { toast } from "react-toastify";
import { hasPermission } from "../../services/utils";
import { PERMISSIONS } from "../../contants/permission";

// Type -> config field descriptors. Adding a new vendor type = one entry here
// (plus the matching adapter + enum on the API).
const TYPE_FIELDS = {
  aws: [
    { key: "region", label: "Region", secret: false },
    { key: "accessKeyId", label: "Access Key ID", secret: true },
    { key: "secretAccessKey", label: "Secret Access Key", secret: true },
    { key: "configurationSetName", label: "Configuration Set", secret: false },
  ],
  simplysend: [
    { key: "apiKey", label: "API Key", secret: true, required: true },
    { key: "id", label: "Account ID", secret: false, required: true },
    { key: "apiUrl", label: "API URL (optional)", secret: false },
    { key: "webhookSecret", label: "Webhook Secret", secret: true },
  ],
};
const TYPES = Object.keys(TYPE_FIELDS);

const emptyForm = () => ({
  name: "",
  type: "aws",
  weight: 0,
  is_active: true,
  max_send_rate: "",
  config: {},
});

function EmailVendors() {
  const dispatch = useDispatch();
  const { list, pagination, loading } = useSelector(
    (state) => state.emailVendorsReducer
  );

  const limit = 10;
  const [filters, setFilters] = useState({ type: "", is_active: "", name: "" });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [page, setPage] = useState(1);
  const totalPages = pagination?.totalPages || 0;

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [testingId, setTestingId] = useState(null);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, page]);

  const load = () =>
    dispatch(fetchEmailVendors({ limit, page, filters: appliedFilters }));

  const userId = () =>
    JSON.parse(localStorage.getItem("user"))?.data?.id;

  // Client-side effective % over the currently-listed active vendors.
  const effectiveFor = useMemo(() => {
    const activeTotal = (list || [])
      .filter((v) => v.is_active && Number(v.weight) > 0)
      .reduce((s, v) => s + Number(v.weight), 0);
    return (v) =>
      v.is_active && Number(v.weight) > 0 && activeTotal > 0
        ? `${Math.round((Number(v.weight) / activeTotal) * 1000) / 10}%`
        : "-";
  }, [list]);

  const applyFilters = () => {
    setAppliedFilters(filters);
    setPage(1);
  };
  const resetFilters = () => {
    setFilters({ type: "", is_active: "", name: "" });
    setAppliedFilters({});
    setPage(1);
  };

  const setConfigField = (state, setState, key, value) =>
    setState({ ...state, config: { ...state.config, [key]: value } });

  const buildConfigPayload = (type, config, { partial } = {}) => {
    const out = {};
    for (const f of TYPE_FIELDS[type] || []) {
      const val = config[f.key];
      if (val === undefined || val === "") {
        // on edit, skip untouched secret fields so the API keeps the stored value
        if (!partial) continue;
        continue;
      }
      out[f.key] = val;
    }
    return out;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        weight: Number(form.weight) || 0,
        is_active: !!form.is_active,
        max_send_rate: form.max_send_rate ? Number(form.max_send_rate) : null,
        config: buildConfigPayload(form.type, form.config),
        createdBy: userId(),
      };
      const res = await dispatch(createEmailVendor(payload)).unwrap();
      if (!res?.success) return toast.error(res?.message || "Failed to create");
      toast.success("Email vendor created");
      setShowCreate(false);
      setForm(emptyForm());
      load();
    } catch (err) {
      toast.error(typeof err === "string" ? err : err?.message || "Failed");
    }
  };

  const openEdit = (v) => {
    setEditForm({
      id: v.id,
      name: v.name,
      type: v.type,
      weight: v.weight,
      is_active: v.is_active,
      max_send_rate: v.max_send_rate ?? "",
      config: {},
    });
    setShowEdit(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: editForm.name.trim(),
        weight: Number(editForm.weight) || 0,
        is_active: !!editForm.is_active,
        max_send_rate: editForm.max_send_rate
          ? Number(editForm.max_send_rate)
          : null,
        updatedBy: userId(),
      };
      const cfg = buildConfigPayload(editForm.type, editForm.config, {
        partial: true,
      });
      if (Object.keys(cfg).length) data.config = cfg;

      const res = await dispatch(
        updateEmailVendor({ id: editForm.id, data })
      ).unwrap();
      if (!res?.success) return toast.error(res?.message || "Failed to update");
      toast.success("Email vendor updated");
      setShowEdit(false);
      load();
    } catch (err) {
      toast.error(typeof err === "string" ? err : err?.message || "Failed");
    }
  };

  const toggleActive = async (v) => {
    try {
      const res = await dispatch(
        updateEmailVendor({
          id: v.id,
          data: { is_active: !v.is_active, updatedBy: userId() },
        })
      ).unwrap();
      if (!res?.success) return toast.error(res?.message || "Failed");
      toast.success(v.is_active ? "Vendor deactivated" : "Vendor activated");
      load();
    } catch (err) {
      toast.error(typeof err === "string" ? err : err?.message || "Failed");
    }
  };

  const runTest = async (v) => {
    setTestingId(v.id);
    try {
      const data = await dispatch(testEmailVendor({ id: v.id })).unwrap();
      if (data?.ok) toast.success(`OK — ${data.detail || "reachable"}`);
      else toast.error(`Failed — ${data?.error || "unreachable"}`);
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Test failed");
    } finally {
      setTestingId(null);
    }
  };

  const renderConfigFields = (state, setState) =>
    (TYPE_FIELDS[state.type] || []).map((f) => (
      <Form.Group className="mb-2" key={f.key}>
        <Form.Label>
          {f.label}
          {f.required ? " *" : ""}
        </Form.Label>
        <Form.Control
          type={f.secret ? "password" : "text"}
          value={state.config[f.key] || ""}
          placeholder={f.secret && state.id ? "•••• unchanged" : ""}
          onChange={(e) =>
            setConfigField(state, setState, f.key, e.target.value)
          }
        />
      </Form.Group>
    ));

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center my-3 px-3">
        <h4 className="fw-semibold mb-0">Email Vendors</h4>
        {hasPermission(PERMISSIONS.EMAIL_VENDOR.CREATE) && (
          <Button variant="primary" onClick={() => setShowCreate(true)}>
            + Add Vendor
          </Button>
        )}
      </div>

      <div className="p-3 border rounded bg-light mx-3 mb-3">
        <Row className="g-2 align-items-end">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={filters.name}
                onChange={(e) =>
                  setFilters({ ...filters, name: e.target.value })
                }
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={filters.type}
                onChange={(e) =>
                  setFilters({ ...filters, type: e.target.value })
                }
              >
                <option value="">All</option>
                {TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filters.is_active}
                onChange={(e) =>
                  setFilters({ ...filters, is_active: e.target.value })
                }
              >
                <option value="">All</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col className="d-flex justify-content-end gap-2 ms-auto">
            <Button className="px-4" onClick={applyFilters}>
              Apply
            </Button>
            <Button variant="secondary" className="px-4" onClick={resetFilters}>
              Reset
            </Button>
          </Col>
        </Row>
      </div>

      <div className="mx-3">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p>Loading...</p>
          </div>
        ) : list?.length ? (
          <>
            <Table bordered hover>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Weight</th>
                  <th>Effective %</th>
                  <th>Max rate</th>
                  <th>Active</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((v, index) => (
                  <tr key={v.id}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{v.name}</td>
                    <td>
                      <span className="badge bg-secondary px-2 py-1">
                        {v.type}
                      </span>
                    </td>
                    <td>{v.weight}</td>
                    <td>{effectiveFor(v)}</td>
                    <td>{v.max_send_rate ?? "default"}</td>
                    <td>
                      <span
                        className={`badge ${
                          v.is_active ? "bg-success" : "bg-secondary"
                        } px-2 py-1`}
                      >
                        {v.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      {v.updatedAt
                        ? new Date(v.updatedAt).toLocaleString()
                        : "NA"}
                    </td>
                    <td className="d-flex gap-1 flex-wrap">
                      {hasPermission(PERMISSIONS.EMAIL_VENDOR.EDIT) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => openEdit(v)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={testingId === v.id}
                            onClick={() => runTest(v)}
                          >
                            {testingId === v.id ? "Testing…" : "Test"}
                          </Button>
                          <Button
                            size="sm"
                            variant={
                              v.is_active
                                ? "outline-danger"
                                : "outline-success"
                            }
                            onClick={() => toggleActive(v)}
                          >
                            {v.is_active ? "Deactivate" : "Activate"}
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <SmartPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={(p) => setPage(p)}
            />
          </>
        ) : (
          <p className="text-center text-muted py-4">No email vendors found</p>
        )}
      </div>

      {/* Create */}
      <Modal show={showCreate} onHide={() => setShowCreate(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Email Vendor</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Form.Group className="mb-2">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Form.Group>
            <Row>
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={form.type}
                    onChange={(e) =>
                      setForm({ ...form, type: e.target.value, config: {} })
                    }
                  >
                    {TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Weight (0-100)</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.weight}
                    onChange={(e) =>
                      setForm({ ...form, weight: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col>
                <Form.Group className="mb-2">
                  <Form.Label>Max send rate (/s, optional)</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.max_send_rate}
                    onChange={(e) =>
                      setForm({ ...form, max_send_rate: e.target.value })
                    }
                  />
                </Form.Group>
              </Col>
              <Col className="d-flex align-items-end mb-2">
                <Form.Check
                  type="switch"
                  label="Active"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm({ ...form, is_active: e.target.checked })
                  }
                />
              </Col>
            </Row>
            <hr />
            <p className="text-muted small mb-2">
              Leave blank to use server defaults / env credentials.
            </p>
            {renderConfigFields(form, setForm)}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Create
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Edit */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Email Vendor</Modal.Title>
        </Modal.Header>
        {editForm && (
          <Form onSubmit={handleUpdate}>
            <Modal.Body>
              <Form.Group className="mb-2">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                  required
                />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Type</Form.Label>
                    <Form.Control value={editForm.type} disabled />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Weight (0-100)</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.weight}
                      onChange={(e) =>
                        setEditForm({ ...editForm, weight: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-2">
                    <Form.Label>Max send rate (/s)</Form.Label>
                    <Form.Control
                      type="number"
                      value={editForm.max_send_rate}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          max_send_rate: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col className="d-flex align-items-end mb-2">
                  <Form.Check
                    type="switch"
                    label="Active"
                    checked={editForm.is_active}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        is_active: e.target.checked,
                      })
                    }
                  />
                </Col>
              </Row>
              <hr />
              <p className="text-muted small mb-2">
                Only fill fields you want to change. Secrets left blank stay as
                stored.
              </p>
              {renderConfigFields(editForm, setEditForm)}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEdit(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Update
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>
    </Layout>
  );
}

export default EmailVendors;
